import { Button, FormGroup, H4, Icon, Tab, Tabs } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import ArrayItemToolbar from "common/form/common/ArrayItemToolbar";
import ColorPickerWidget from "common/form/widgets/ColorPickerWidget";
import ButtonSelect from "common/form/widgets/common/ButtonSelect";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import CssUnitWidget from "common/form/widgets/CssUnitWidget";
import SpaceWidget from "common/form/widgets/SpaceWidget";
import VerticalCenter from "common/fragments/VerticalCenter";
import { PositionProperty, TextAlignProperty, WhiteSpaceProperty } from "csstype";
import { IFieldProps } from "decorator-form";
import React from "react";
import { ILayoutType, LayoutTypeRegistry } from "../ILayoutType";


interface IFixedItem {
	position?: "static" | "absolute" | "fixed";
	top?: string;
	left?: string;
	right?: string;
	bottom?: string;
	height?: string;
	width?: string;
	backgroundColor?: string;
	regionUuid?: string;
}

interface IFixedLayout {
	padding: string;
	regions: IFixedItem[];
}

const styles = {
	container: {
		position: 'relative' as PositionProperty,
		minHeight: '250px',
		maxHeight: '350px',
		overflow: 'hidden',
		backgroundColor: "rgba(0,0,0,0.2)",
		border: "1px solid rgba(0,0,0,0.3)"
	},
	region: {
		backgroundColor: "rgba(19,124,189,0.2)",
		border: "1px solid rgba(19,124,189,0.3)",
		borderRadius: "4px",
		color: "rgb(19,124,189)",
		minHeight: "12px",
		whiteSpace: 'nowrap' as WhiteSpaceProperty,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		textAlign: 'center' as TextAlignProperty,
		maxWidth: '100%',
		maxHeight: '100%'
	}
};

const positionOptions = [
	{value: 'static', label: 'Static'},
	{value: 'absolute', label: 'Absolute (relative to container)'},
	{value: 'fixed', label: 'Fixed (relative to viewport)'}
];

class FixedLayoutField extends React.Component<IFieldProps<IFixedLayout> & { regions: { [uuid: string]: { __editName: string; } } }> {

	public render() {
		const { formData = {} as IFixedLayout, regions } = this.props;

		return (
			<Tabs id="layout:fixed">
				<Tab id="container" title="Container" panel={
					<div>
						<FormGroup label="Container Padding">
							<SpaceWidget onChange={this.onChangeField('padding')} value={formData.padding} />
						</FormGroup>
					</div>
				} />
				<Tab id="items" title="Items" panel={
					<FormGroup>
						{
							formData.regions && formData.regions.map((region, ri) => (
								<FormGroup key={ri}>
									<div style={{display: 'flex', justifyContent: 'space-between'}}>
										<H4>{`Region ${ri + 1}`}</H4>
										<ArrayItemToolbar itemIndex={ri} arr={formData.regions} onChange={this.onChangeRegions} />
									</div>
									<FormGroup label="Region">
										<DropdownSelect
											value={region.regionUuid}
											onChange={this.onChangeRegionField(ri, 'regionUuid')}
											options={Object.entries(regions || {}).map(([uuid, reg]) => ({
												value: uuid,
												label: reg.__editName || "Untitled Region"
											}))}
										/>
									</FormGroup>
									<FormGroup label="Position">
										<ButtonSelect
											value={region.position}
											onChange={this.onChangeRegionField(ri, 'position')}
											options={positionOptions}
										/>
									</FormGroup>
									<FormGroup label="Top">
										<CssUnitWidget value={region.top} onChange={this.onChangeRegionField(ri, 'top')} />
									</FormGroup>
									<FormGroup label="Left">
										<CssUnitWidget value={region.left} onChange={this.onChangeRegionField(ri, 'left')} />
									</FormGroup>
									<FormGroup label="Right">
										<CssUnitWidget value={region.right} onChange={this.onChangeRegionField(ri, 'right')} />
									</FormGroup>
									<FormGroup label="Bottom">
										<CssUnitWidget value={region.bottom} onChange={this.onChangeRegionField(ri, 'bottom')} />
									</FormGroup>
									<FormGroup label="Width">
										<CssUnitWidget value={region.width} onChange={this.onChangeRegionField(ri, 'width')} />
									</FormGroup>
									<FormGroup label="Height">
										<CssUnitWidget value={region.height} onChange={this.onChangeRegionField(ri, 'height')} />
									</FormGroup>
									<FormGroup label="Background Color">
										<ColorPickerWidget value={region.backgroundColor} onChange={this.onChangeRegionField(ri, 'backgroundColor')} />
									</FormGroup>
								</FormGroup>
							))
						}
						<FormGroup>
							<Button small={true} text="Add Region" onClick={this.addRegion} />
						</FormGroup>
					</FormGroup>
				} />
			</Tabs>
		);
	}

	private addRegion = () => {
		const { formData = {} as IFixedLayout, onChange } = this.props;
		onChange({...formData, regions: [...formData.regions || [], {position: 'static'}]});
	}

	private onChangeField = (field: string) => (value: any) => {
		const { formData = {} as IFixedLayout, onChange } = this.props;
		onChange({...formData, [field]: value});
	}

	private onChangeRegions = (regions: IFixedItem[]) => {
		const { formData = {} as IFixedLayout, onChange } = this.props;
		onChange({...formData, regions});
	}

	private onChangeRegionField = (index: number, field: string) => (value: any) => {
		const { formData = {} as IFixedLayout, onChange } = this.props;
		onChange({
			...formData,
			regions: formData.regions.map((r, ri) => ri === index ? {...r, [field]: value} : r)
		});
	}
}

const FixedLayout: ILayoutType<IFixedLayout> = {
	type: "fixed",
	label: "Fixed",
	icon: IconNames.HEATMAP,
	renderPreview: (data, regions) => (
		<div style={styles.container}>
			{data.regions && data.regions.map(({regionUuid, position, ...restRegion}, ri) => (
				<div key={ri} style={{...restRegion, ...styles.region, position: position === 'fixed' ? 'absolute' : position}}>
					<VerticalCenter style={{height: '100%'}}>
						{
							position === 'fixed' && <Icon icon={IconNames.PIN} />
						}
						{
							regionUuid ? (
								regions[regionUuid] && regions[regionUuid].__editName || "Invalid Region!"
							) : "(Select a Region)"
						}
					</VerticalCenter>
				</div>
			))}
		</div>
	),
	renderLayout: (data, renderRegion) => (
		<div style={{position: 'relative', padding: data.padding}}>
			{data.regions && data.regions.map(({regionUuid, ...restRegion}, ri) => (
				<div key={ri} style={restRegion}>
					{renderRegion(regionUuid, ri)}
				</div>
			))}
		</div>
	),
	renderInput: (data, onChange, regions) => (
		<FixedLayoutField formData={data} regions={regions} onChange={onChange} uiSchema={undefined} idSchema={undefined} />
	)
};

export default LayoutTypeRegistry.registerType(FixedLayout);



// WEBPACK FOOTER //
// ./src/common/form/fields/layout/layoutTypes/FixedLayout.tsx